<template>
  <div class="page-wrapper">
    <ExamHeader />

    <div class="exam-body">

      <!-- ── Countdown Bar ── -->
      <div class="timer-bar" :class="{ 'timer-warning': timerWarning }">
        <span class="timer-icon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
        </span>
        <countdown
          ref="countdown"
          :auto-start="false"
          :time="time"
          @progress="handleCountdownProgress"
        >
          <template slot-scope="props">
            {{ props.minutes }}m {{ props.seconds }}s remaining
          </template>
        </countdown>
      </div>

      <!-- ── Instructions Screen ── -->
      <div v-if="!start" class="instructions-card">
        <div class="instructions-icon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
        </div>
        <h2 class="instructions-title">Examination Rules</h2>
        <p class="instructions-intro">Welcome to the Royal Ambassadors Examination Portal. Please read the following rules carefully before proceeding.</p>
        <ul class="rules-list">
          <li>
            <span class="rule-num">01</span>
            <span>Only attempt this examination once — duplicate attempts will be voided.</span>
          </li>
          <li>
            <span class="rule-num">02</span>
            <span>This examination lasts for <strong>60 minutes</strong> only.</span>
          </li>
          <li>
            <span class="rule-num">03</span>
            <span>You may submit early if you finish before time elapses.</span>
          </li>
          <li>
            <span class="rule-num">04</span>
            <span>Click <strong>Start Examination</strong> only when you are fully ready.</span>
          </li>
        </ul>
        <button @click.prevent="startTimer" class="start-btn">
          Start Examination
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
        </button>
      </div>

      <!-- ── Active Exam ── -->
      <div v-if="start && !timeUp" class="exam-layout">

        <!-- Question Navigator -->
        <div class="nav-panel">
          <div class="nav-header">
            <button class="nav-toggle" @click="showQuestions = !showQuestions">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
              {{ showQuestions ? 'Hide' : 'Show' }} Questions
            </button>
          </div>
          <div class="nav-grid" :class="{ 'nav-grid--hidden': !showQuestions }">
            <button
              v-for="(question, i) in questions"
              :key="i"
              @click.prevent="goTo(i)"
              :class="[
                'nav-btn',
                index === i ? 'nav-btn--active' : '',
                question.selected ? 'nav-btn--answered' : '',
              ]"
            >{{ i + 1 }}</button>
          </div>
          <div class="nav-legend">
            <span class="legend-item"><span class="legend-dot legend-dot--answered"></span> Answered</span>
            <span class="legend-item"><span class="legend-dot legend-dot--active"></span> Current</span>
            <span class="legend-item"><span class="legend-dot legend-dot--unanswered"></span> Unanswered</span>
          </div>
        </div>

        <!-- Question Panel -->
        <div class="question-panel">
          <div class="question-meta">
            <span class="question-badge">Question {{ index + 1 }} of {{ questions.length }}</span>
            <span class="question-type-badge">{{ questions[index].type === 'objective' ? 'Multiple Choice' : 'Written' }}</span>
          </div>

          <p class="question-text">{{ questions[index].question }}</p>

          <!-- Objective Options -->
          <div class="options-list">
            <label
              v-for="opt in ['a','b','c','d']"
              :key="opt"
              :class="['option-label', questions[index].selected === opt ? 'option-label--selected' : '']"
              :for="`opt-${opt}`"
            >
              <input
                type="radio"
                :id="`opt-${opt}`"
                :value="opt"
                v-model="questions[index].selected"
                class="option-radio"
              />
              <span class="option-key">{{ opt.toUpperCase() }}</span>
              <span class="option-text">{{ questions[index].options[opt] }}</span>
            </label>
          </div>

          <!-- Written Answer -->
          <!-- <div v-else class="textarea-wrapper">
            <textarea
              class="answer-textarea"
              rows="5"
              placeholder="Type your answer here…"
              v-model.trim="questions[index].selected"
            />
          </div> -->

          <!-- Navigation Buttons -->
          <div class="question-nav">
            <button
              @click="prev"
              :disabled="index === 0"
              class="nav-arrow-btn"
              :class="{ 'nav-arrow-btn--disabled': index === 0 }"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
              Previous
            </button>
            <button
              v-if="index < questions.length - 1"
              @click="next"
              class="nav-arrow-btn nav-arrow-btn--next"
            >
              Next
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
            </button>
            <button
              v-else
               @click="checkAndFinish"
              class="submit-btn"
            >
              Submit All
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            </button>
          </div>
        </div>
      </div>

      <!-- ── Time Up Screen ── -->
      <div v-if="timeUp" class="timeup-card">
        <div class="timeup-icon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
        </div>
        <h2 class="timeup-title">Time's Up!</h2>
        <p class="timeup-subtitle">Your time has elapsed. Please submit your answers now.</p>
        <button @click="showComplete" class="submit-btn submit-btn--large">
          Submit Answers
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        </button>
      </div>

    </div>

    <!-- ── Confirm Submit Modal ── -->
    <transition name="modal-fade">
      <div v-if="finish" class="modal-overlay" @click.self="finish = false">
        <div class="modal-card">
          <div class="modal-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          </div>
          <h3 class="modal-title">Submit Examination?</h3>
          <p class="modal-body">Are you sure you want to submit? <strong>This action cannot be reversed.</strong></p>
          <div class="modal-actions">
            <button @click="finish = false" class="modal-cancel-btn">Go Back</button>
            <button @click="showComplete" class="modal-confirm-btn">
              Yes, Submit
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            </button>
          </div>
        </div>
      </div>
    </transition>

    <transition name="modal-fade">
  <div v-if="warnFinish" class="modal-overlay" @click.self="warnFinish = false">
    <div class="modal-card">
      <div class="modal-icon" style="background: #fff0f0; color: #c84040;">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
      </div>
      <h3 class="modal-title">Unanswered Questions</h3>
      <p class="modal-body">
        You have <strong>{{ unansweredCount }} unanswered question(s)</strong>. 
        Are you sure you want to submit without completing them?
      </p>
      <div class="modal-actions">
        <button @click="warnFinish = false" class="modal-cancel-btn">Go Back</button>
        <button @click="warnFinish = false; finish = true" class="modal-confirm-btn" style="background: linear-gradient(135deg, #b84040, #d45555); box-shadow: 0 3px 10px rgba(184,64,64,0.25);">
          Submit Anyway
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        </button>
      </div>
    </div>
  </div>
</transition>

    <vue-element-loading v-if="loading" :active="loading" :is-full-screen="true" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import ExamHeader from "../components/ExamHeader";
const shuffle = require("shuffle-array");

export default {
  components: { ExamHeader },
  data() {
    return {
      data: {},
      user: {},
      start: false,
      time: 60 * 60 * 1000,
      noOfQuestions: 50,
      loading: false,
      errors: {},
      finish: false,
      index: 0,
      timeUp: false,
      disabled: false,
      questions: [],
      showQuestions: true,
      timerWarning: false,
      warnFinish: false,
unansweredCount: 0,
    };
  },
  computed: {
    ...mapGetters(["getQuestions", "getQuestionAnswers"]),
  },
  mounted() {
    this.user = JSON.parse(localStorage.getItem("ra-user"));
    let started = localStorage.getItem("exam-started");
    if (started) {
      this.questions = JSON.parse(localStorage.getItem("exam-questions"));
      this.time = JSON.parse(localStorage.getItem("exam-time"));
      this.$refs.countdown.start();
      this.loading = false;
      this.start = true;
      if (this.time < 1001) {
        this.timeUp = true;
      }
    }
  },
  methods: {
checkAndFinish() {
  const unanswered = this.questions.filter(q => !q.selected).length;
  if (unanswered > 0) {
    this.unansweredCount = unanswered;
    this.warnFinish = true;
  } else {
    this.finish = true;
  }
},
    handleCountdownProgress(data) {
      localStorage.setItem("exam-time", data.totalMilliseconds);
      this.timerWarning = data.totalMilliseconds < 5 * 60 * 1000;
      if (data.totalMilliseconds < 1000) {
        this.timeUp = true;
      }
      localStorage.setItem("exam-started", true);
    },
    ...mapActions(["fetchQuestions", "submit"]),
    handleTimeExpire() {
      this.timeUp = true;
    },
    async startTimer() {
      this.loading = true;
      let res = await this.fetchQuestions();
      if (res.status == 400) {
        this.$swal("Error!", "You have already taken this exam", "error");
        localStorage.clear();
        this.$router.push("/login");
        return;
      }
      let ques = res.data.questions;
      let questions = [];
      ques.forEach((asset) => {
        questions.push({
          question: asset.question,
          answer: asset.answer,
          mode: asset.mode,
          type: asset.type,
          options: {
            a: asset.option_a,
            b: asset.option_b,
            c: asset.option_c,
            d: asset.option_d,
            // e: "None of the above",
          },
          selected: "",
        });
      });
      this.questions = shuffle(questions);
      localStorage.setItem("exam-started", true);
      localStorage.setItem("exam-questions", JSON.stringify(this.questions));
      this.$refs.countdown.start();
      this.loading = false;
      this.start = true;
    },
    next() {
      this.index++;
      localStorage.setItem("exam-questions", JSON.stringify(this.questions));
    },
    prev() {
      this.index--;
      localStorage.setItem("exam-questions", JSON.stringify(this.questions));
    },
    goTo(i) {
      this.index = i;
      localStorage.setItem("exam-questions", JSON.stringify(this.questions));
    },
    async showComplete() {
      this.loading = true;
      this.timeUp = true;
      this.time = 0;
      await this.submit({
        user: this.user._id,
        noOfQuestions: this.noOfQuestions,
        questions: JSON.stringify(this.questions),
      });
      this.finish = false;
      this.$swal("Success!", "You have successfully submitted.", "success");
      localStorage.clear();
      this.$router.push("/login");
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600;700&family=Jost:wght@300;400;500;600&display=swap');

/* ── Base ── */
* { box-sizing: border-box; }

.page-wrapper {
  min-height: 100vh;
  background: #f4f5f9;
  font-family: 'Jost', sans-serif;
  color: #1a1a2e;
}

/* ── Timer Bar ── */
.timer-bar {
  background: #1a2a3e;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.65rem 1rem;
  font-size: 0.9rem;
  font-weight: 500;
  letter-spacing: 0.04em;
  transition: background 0.4s;
}
.timer-bar.timer-warning {
  background: linear-gradient(90deg, #b84040, #d45555);
  animation: pulse-bg 1.5s ease-in-out infinite;
}
@keyframes pulse-bg {
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.85; }
}
.timer-icon svg { width: 16px; height: 16px; }

/* ── Exam Body ── */
.exam-body {
  max-width: 1100px;
  margin: 0 auto;
  padding: 1.5rem 1rem 3rem;
}

/* ── Instructions Card ── */
.instructions-card {
  max-width: 600px;
  margin: 2rem auto 0;
  background: #fff;
  border-radius: 16px;
  padding: 2.5rem 2.5rem 2rem;
  box-shadow: 0 0 0 1px rgba(200,169,110,0.12), 0 16px 48px rgba(0,0,0,0.1);
  text-align: center;
  animation: slideUp 0.45s cubic-bezier(0.22,1,0.36,1) both;
}
.instructions-icon {
  width: 56px; height: 56px;
  background: linear-gradient(135deg, #f0e8d6, #fdf6e8);
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 1.25rem;
  color: #c8a96e;
}
.instructions-icon svg { width: 26px; height: 26px; }
.instructions-title {
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.8rem;
  font-weight: 700;
  margin: 0 0 0.5rem;
}
.instructions-intro {
  font-size: 0.88rem;
  color: #6b7090;
  margin-bottom: 1.5rem;
  line-height: 1.6;
}
.rules-list {
  list-style: none;
  padding: 0; margin: 0 0 2rem;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}
.rules-list li {
  display: flex;
  gap: 0.85rem;
  align-items: flex-start;
  font-size: 0.9rem;
  line-height: 1.55;
  color: #3a3f5c;
}
.rule-num {
  flex-shrink: 0;
  width: 28px; height: 28px;
  background: linear-gradient(135deg, #1a2a3e, #243650);
  color: #c8a96e;
  border-radius: 6px;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.05em;
}
.start-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.8rem 2rem;
  background: linear-gradient(135deg, #1a2a3e, #243650);
  color: #fff;
  font-family: 'Jost', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
  box-shadow: 0 4px 16px rgba(26,42,62,0.25);
}
.start-btn svg { width: 16px; height: 16px; }
.start-btn:hover {
  background: linear-gradient(135deg, #c8a96e, #e8c98a);
  transform: translateY(-1px);
  box-shadow: 0 6px 24px rgba(200,169,110,0.35);
}

/* ── Exam Layout ── */
.exam-layout {
  display: grid;
  grid-template-columns: 220px 1fr;
  gap: 1.25rem;
  margin-top: 1.5rem;
  align-items: start;
}

/* ── Nav Panel ── */
.nav-panel {
  background: #fff;
  border-radius: 14px;
  padding: 1.25rem;
  box-shadow: 0 2px 16px rgba(0,0,0,0.07);
  position: sticky;
  top: 1rem;
}
.nav-header { margin-bottom: 1rem; }
.nav-toggle {
  display: none;
  width: 100%;
  align-items: center;
  gap: 0.4rem;
  background: #f0f2f8;
  border: none;
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  font-family: 'Jost', sans-serif;
  font-size: 0.82rem;
  font-weight: 600;
  color: #4a4f6a;
  cursor: pointer;
}
.nav-toggle svg { width: 14px; height: 14px; }
.nav-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}
.nav-grid--hidden { display: none; }
.nav-btn {
  width: 36px; height: 36px;
  border: none;
  border-radius: 8px;
  background: #f0f2f8;
  color: #6b7090;
  font-family: 'Jost', sans-serif;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, transform 0.1s;
}
.nav-btn:hover { background: #e2e5f0; transform: scale(1.05); }
.nav-btn--active { background: #1a2a3e !important; color: #fff !important; }
.nav-btn--answered { background: #2d7a4f; color: #fff; }
.nav-legend {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #eee;
}
.legend-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.72rem;
  color: #8a8fa8;
  font-weight: 500;
}
.legend-dot {
  width: 10px; height: 10px;
  border-radius: 3px;
  flex-shrink: 0;
}
.legend-dot--answered  { background: #2d7a4f; }
.legend-dot--active    { background: #1a2a3e; }
.legend-dot--unanswered{ background: #f0f2f8; border: 1px solid #d0d5e8; }

/* ── Question Panel ── */
.question-panel {
  background: #fff;
  border-radius: 14px;
  padding: 2rem;
  box-shadow: 0 2px 16px rgba(0,0,0,0.07);
  animation: slideUp 0.3s ease both;
}
.question-meta {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
}
.question-badge {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8a8fa8;
}
.question-type-badge {
  font-size: 0.7rem;
  font-weight: 600;
  background: #f0e8d6;
  color: #a07840;
  padding: 0.2rem 0.6rem;
  border-radius: 20px;
  letter-spacing: 0.04em;
}
.question-text {
  font-size: 1.05rem;
  font-weight: 500;
  line-height: 1.65;
  color: #1a1a2e;
  margin-bottom: 1.5rem;
  padding-bottom: 1.25rem;
  border-bottom: 1px solid #f0f2f8;
}

/* ── Options ── */
.options-list {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  margin-bottom: 1.75rem;
}
.option-label {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 0.75rem 1rem;
  border: 1.5px solid #e2e5f0;
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
  user-select: none;
}
.option-label:hover {
  border-color: #c8a96e;
  background: #fdf9f2;
}
.option-label--selected {
  border-color: #1a2a3e;
  background: #f0f4f8;
  box-shadow: 0 0 0 3px rgba(26,42,62,0.08);
}
.option-radio { display: none; }
.option-key {
  flex-shrink: 0;
  width: 28px; height: 28px;
  background: #f0f2f8;
  color: #4a4f6a;
  border-radius: 7px;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.78rem;
  font-weight: 700;
  transition: background 0.15s, color 0.15s;
}
.option-label--selected .option-key {
  background: #1a2a3e;
  color: #c8a96e;
}
.option-text {
  font-size: 0.9rem;
  color: #3a3f5c;
  line-height: 1.5;
}

/* ── Textarea ── */
.textarea-wrapper { margin-bottom: 1.75rem; }
.answer-textarea {
  width: 100%;
  padding: 0.85rem 1rem;
  font-family: 'Jost', sans-serif;
  font-size: 0.9rem;
  color: #1a1a2e;
  background: #f7f8fc;
  border: 1.5px solid #e2e5f0;
  border-radius: 10px;
  outline: none;
  resize: vertical;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.answer-textarea:focus {
  border-color: #c8a96e;
  box-shadow: 0 0 0 3px rgba(200,169,110,0.12);
  background: #fff;
}
.answer-textarea::placeholder { color: #b8bdce; }

/* ── Question Navigation ── */
.question-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
}
.nav-arrow-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 1.25rem;
  background: #f0f2f8;
  color: #4a4f6a;
  font-family: 'Jost', sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  border: none;
  border-radius: 9px;
  cursor: pointer;
  transition: background 0.15s, transform 0.1s;
}
.nav-arrow-btn svg { width: 15px; height: 15px; }
.nav-arrow-btn:hover:not(:disabled) { background: #e2e5f0; transform: translateX(-2px); }
.nav-arrow-btn--next:hover:not(:disabled) { transform: translateX(2px); }
.nav-arrow-btn--disabled { opacity: 0.4; cursor: not-allowed; }

/* ── Submit Button ── */
.submit-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.7rem 1.5rem;
  background: linear-gradient(135deg, #2d7a4f, #3a9960);
  color: #fff;
  font-family: 'Jost', sans-serif;
  font-size: 0.88rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  border: none;
  border-radius: 9px;
  cursor: pointer;
  transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
  box-shadow: 0 4px 14px rgba(45,122,79,0.25);
}
.submit-btn svg { width: 15px; height: 15px; }
.submit-btn:hover { background: linear-gradient(135deg, #25673f, #2f7d4e); transform: translateY(-1px); }
.submit-btn--large { padding: 0.85rem 2rem; font-size: 0.95rem; }

/* ── Time Up Card ── */
.timeup-card {
  max-width: 480px;
  margin: 3rem auto 0;
  background: #fff;
  border-radius: 16px;
  padding: 2.5rem;
  box-shadow: 0 0 0 1px rgba(184,64,64,0.12), 0 16px 48px rgba(0,0,0,0.1);
  text-align: center;
  animation: slideUp 0.45s cubic-bezier(0.22,1,0.36,1) both;
}
.timeup-icon {
  width: 60px; height: 60px;
  background: #fff0f0;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 1.25rem;
  color: #c84040;
}
.timeup-icon svg { width: 28px; height: 28px; }
.timeup-title {
  font-family: 'Cormorant Garamond', serif;
  font-size: 2rem;
  font-weight: 700;
  color: #c84040;
  margin: 0 0 0.5rem;
}
.timeup-subtitle {
  font-size: 0.9rem;
  color: #6b7090;
  margin-bottom: 1.5rem;
}

/* ── Modal ── */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(10,15,25,0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}
.modal-card {
  background: #fff;
  border-radius: 16px;
  padding: 2rem 2rem 1.75rem;
  max-width: 420px;
  width: 100%;
  box-shadow: 0 24px 64px rgba(0,0,0,0.3);
  text-align: center;
  animation: modalPop 0.3s cubic-bezier(0.34,1.56,0.64,1) both;
}
@keyframes modalPop {
  from { opacity: 0; transform: scale(0.92) translateY(10px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
}
.modal-icon {
  width: 52px; height: 52px;
  background: #fff8e6;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 1rem;
  color: #c8a96e;
}
.modal-icon svg { width: 24px; height: 24px; }
.modal-title {
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.6rem;
  font-weight: 700;
  margin: 0 0 0.5rem;
}
.modal-body {
  font-size: 0.9rem;
  color: #6b7090;
  line-height: 1.6;
  margin-bottom: 1.5rem;
}
.modal-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: center;
}
.modal-cancel-btn {
  padding: 0.65rem 1.25rem;
  background: #f0f2f8;
  color: #4a4f6a;
  font-family: 'Jost', sans-serif;
  font-size: 0.88rem;
  font-weight: 600;
  border: none;
  border-radius: 9px;
  cursor: pointer;
  transition: background 0.15s;
}
.modal-cancel-btn:hover { background: #e2e5f0; }
.modal-confirm-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.65rem 1.4rem;
  background: linear-gradient(135deg, #2d7a4f, #3a9960);
  color: #fff;
  font-family: 'Jost', sans-serif;
  font-size: 0.88rem;
  font-weight: 600;
  border: none;
  border-radius: 9px;
  cursor: pointer;
  transition: background 0.15s, transform 0.1s;
  box-shadow: 0 3px 10px rgba(45,122,79,0.25);
}
.modal-confirm-btn svg { width: 14px; height: 14px; }
.modal-confirm-btn:hover { background: linear-gradient(135deg, #25673f, #2f7d4e); }

/* ── Transition ── */
.modal-fade-enter-active, .modal-fade-leave-active { transition: opacity 0.2s; }
.modal-fade-enter, .modal-fade-leave-to { opacity: 0; }

/* ── Shared Animation ── */
@keyframes slideUp {
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ── Mobile Responsive ── */
@media (max-width: 768px) {
  .exam-layout {
    grid-template-columns: 1fr;
  }
  .nav-panel {
    position: static;
  }
  .nav-toggle {
    display: flex;
  }
  .nav-legend {
    flex-direction: row;
    gap: 1rem;
    flex-wrap: wrap;
  }
}

@media (max-width: 500px) {
  .exam-body { padding: 1rem 0.75rem 2rem; }
  .question-panel { padding: 1.25rem; }
  .instructions-card { padding: 1.75rem 1.25rem 1.5rem; }
  .question-nav { flex-direction: column; }
  .nav-arrow-btn, .submit-btn { width: 100%; justify-content: center; }
  .modal-actions { flex-direction: column; }
  .modal-cancel-btn, .modal-confirm-btn { width: 100%; justify-content: center; }
}
</style>